<template>
  <div class="user-form">
    <Tabs v-model:value="activeTab">
      <TabList>
        <Tab :value="0" data-testid="users-form-tab-info">{{ t('Informações') }}</Tab>
        <Tab :value="1" data-testid="users-form-tab-groups">{{ t('Permissões') }}</Tab>
        <Tab :value="2" data-testid="users-form-tab-franchises">{{ t('Franquias') }}</Tab>
      </TabList>

      <TabPanels>
        <!-- Info Tab -->
        <TabPanel :value="0">
          <form data-testid="users-form-info" @submit.prevent="handleSubmit">
            <div class="form-group">
              <div class="input-group">
                <span class="input-group-addon" style="white-space:nowrap;">
                  {{ t('Usuário') }}
                </span>
                <div class="form-control-static">{{ formData.username }}</div>
              </div>
            </div>

            <input-text
              v-model="formData.name"
              elem-id="name"
              :label="t('Nome')"
              :placeholder="t('Nome')"
              :required="true"
              :validation="() => formData.name.trim().length > 0"
              data-testid="users-form-name"
            />

            <input-text
              v-model="formData.documento"
              elem-id="documento"
              :label="t('Documento (CPF, passaporte ou RNE)')"
              :placeholder="t('Documento')"
              data-testid="users-form-documento"
            />

            <input-text
              v-model="formData.email"
              elem-id="email"
              type="email"
              :label="t('Email')"
              :placeholder="t('Email')"
              :required="true"
              :validation="() => formData.email.trim().length > 0"
              data-testid="users-form-email"
            />

            <input-phone
              v-model="phoneModel"
              elem-id="phone"
              :label="t('Telefone')"
              :placeholder="t('Telefone')"
              :language="currentLanguage"
              data-testid="users-form-phone"
            />

            <select-list
              v-model="formData.language"
              elem-id="language"
              :label="t('Idioma')"
              :itens-select="languageOptions"
              data-testid="users-form-language"
            />

            <color-picker
              v-model="formData.bgcolor"
              elem-id="bgcolor"
              :label="t('Cor de Fundo')"
              data-testid="users-form-bgcolor"
            />

            <select-list
              v-model="formData.versaoSistema"
              elem-id="versaoSistema"
              :label="t('Versão do Sistema')"
              :itens-select="versaoSistemaOptions"
              data-testid="users-form-versao-sistema"
            />
          </form>
        </TabPanel>

        <!-- Groups Tab -->
        <TabPanel :value="1">
          <div v-if="loadingGroups" class="text-center" style="padding: 20px;">
            <i class="pi pi-spin pi-spinner"></i> {{ t('Carregando...') }}
          </div>
          <div v-else-if="availableGroups.length === 0" class="info-message">
            {{ t('Nenhum grupo disponível') }}
          </div>
          <permissoes-da-pessoa
            v-else
            v-model="userGroups"
            :available-groups="availableGroups"
            :disabled="isTogglingAnyGroup"
            data-testid="users-form-groups"
          />
        </TabPanel>

        <!-- Franchises Tab -->
        <TabPanel :value="2">
          <div v-if="loadingFranchises" class="text-center" style="padding: 20px;">
            <i class="pi pi-spin pi-spinner"></i> {{ t('Carregando...') }}
          </div>
          <div v-else-if="availableFranchises.length === 0" class="info-message">
            {{ t('Nenhuma franquia disponível') }}
          </div>
          <button-list-multiple
            v-else
            v-model="userFranchises"
            :itensSelect="availableFranchisesFormatted"
            elemId="user-franchises-list"
            :label="t('Franquias')"
            :searchPlaceholder="t('Buscar franquias...')"
            :disabled="isTogglingAnyFranchise"
            data-testid="users-form-franchises"
          />
        </TabPanel>
      </TabPanels>
    </Tabs>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useUsers } from '@/composables/useUsers';
import { useLanguages } from '@/composables/useLanguages';
import { permissionsService } from '@/services/permissionsService';
import { eventBus } from '@/eventBus';
import { log } from '@/utils/DebugLogger';
import { phoneFromString, phoneToString } from '@/composables/usePhoneFormat';
import PermissoesDaPessoa from './acesso/PermissoesDaPessoa.vue';

interface User {
  username?: string;
  sub?: string;
  name?: string;
  documento?: string;
  email?: string;
  phone_number?: string;
  language?: string;
  bgcolor?: string;
  versaoSistema?: string | number;
  Usuario?: User;
  [key: string]: unknown;
}

interface FranchiseEntry {
  Dados?: { Nome?: string; [key: string]: unknown };
  Nome?: string;
  [key: string]: unknown;
}

interface PhoneModel {
  countryCode: string;
  areaCode: string;
  phoneNumber: string;
}

interface LanguageOption {
  ID: string;
  Name: string;
}

interface SelectableOption {
  ID: string;
  Name: string;
}

interface AvailableFranchise {
  id: string;
  nome: string;
}

interface UserPermissions {
  Groups?: string[];
  groups?: string[];
  Franquias?: Record<string, unknown>;
  franquias?: Record<string, unknown>;
}

const { t, locale } = useI18n();

// Props
const props = defineProps<{
  user: User;
}>();

// Data
const activeTab = ref<number>(0);

// Emits
const emit = defineEmits<{
  'save-success': [];
  'user-loaded': [user: User];
}>();

// Composables
const { fetchUserBySub, updateUser, fetchUserGroups, fetchUserPermissions, toggleUserGroup, toggleUserFranquia } = useUsers();
const { fetchLanguages } = useLanguages();

// Data
const formData = ref<{
  username: string;
  name: string;
  documento: string;
  email: string;
  phone_number: string;
  language: string;
  bgcolor: string;
  versaoSistema: string;
}>({
  username: '',
  name: '',
  documento: '',
  email: '',
  phone_number: '',
  language: 'pt-BR',
  bgcolor: '#3c8dbc',
  versaoSistema: '0'
});

const phoneModel = ref<PhoneModel>({
  countryCode: '+55',
  areaCode: '',
  phoneNumber: ''
});

const languageOptions = ref<LanguageOption[]>([]);

const versaoSistemaOptions = ref<SelectableOption[]>([
  { ID: '0', Name: 'Versão 0 (Padrão)' },
  { ID: '1', Name: 'Versão 1 (Avançada)' }
]);

const isSubmitting = ref<boolean>(false);
const loadingGroups = ref<boolean>(false);
const loadingFranchises = ref<boolean>(false);
const loadingLanguages = ref<boolean>(false);
const availableGroups = ref<string[]>([]);
const availableFranchises = ref<AvailableFranchise[]>([]);
const userGroups = ref<string[]>([]);
const userFranchises = ref<string[]>([]);
const togglingGroups = ref<Record<string, boolean>>({});
const togglingFranchises = ref<Record<string, boolean>>({});

// Computed
const currentLanguage = computed<string>(() => {
  const languageMap: Record<string, string> = {
    'pt-BR': 'ptBR',
    'es-ES': 'ES',
    'en-US': 'enUS'
  };
  return languageMap[locale.value] || 'ptBR';
});

const isFormValid = computed<boolean>(() => {
  return formData.value.name.trim().length > 0 &&
         formData.value.email.trim().length > 0;
});

// Format franchises for ButtonListMultiple (already has id and nome, convert to {ID, Name})
const availableFranchisesFormatted = computed<SelectableOption[]>(() => {
  return availableFranchises.value.map(franchise => ({
    ID: franchise.id,
    Name: franchise.nome
  }));
});

// Check if any group is being toggled
const isTogglingAnyGroup = computed<boolean>(() => {
  return Object.values(togglingGroups.value).some(val => val === true);
});

// Check if any franchise is being toggled
const isTogglingAnyFranchise = computed<boolean>(() => {
  return Object.values(togglingFranchises.value).some(val => val === true);
});

const canSave = computed<boolean>(() => {
  return isFormValid.value && !isSubmitting.value;
});

// Track previous values to detect changes
let previousGroups: string[] = [];
let previousFranchises: string[] = [];

// Watch for group changes from ButtonListMultiple
watch(userGroups, async (newGroups) => {
  // Find what changed
  const added = newGroups.filter(g => !previousGroups.includes(g));
  const removed = previousGroups.filter(g => !newGroups.includes(g));

  // Update previous groups
  previousGroups = [...newGroups];

  // Trocar a função da pessoa muda dois grupos de uma vez (sai um, entra outro): todos os que
  // mudaram são gravados, em sequência — tirar antes de dar, para a conta nunca acumular os dois.
  for (const changedGroup of [...removed, ...added]) {
    log('UserForm', 'watch:userGroups', 'Group changed', changedGroup);
    await handleToggleGroup(changedGroup);
  }
}, { deep: true });

// Watch for franchise changes from ButtonListMultiple
watch(userFranchises, async (newFranchises) => {
  // Find what changed
  const added = newFranchises.filter(f => !previousFranchises.includes(f));
  const removed = previousFranchises.filter(f => !newFranchises.includes(f));

  // Update previous franchises
  previousFranchises = [...newFranchises];

  // Handle toggle for the changed franchise
  const changedFranchise = added.length > 0 ? added[0] : removed[0];
  if (changedFranchise) {
    log('UserForm', 'watch:userFranchises', 'Franchise changed', changedFranchise);
    await handleToggleFranchise(changedFranchise);
  }
}, { deep: true });

// Methods
const loadLanguages = async (): Promise<void> => {
  loadingLanguages.value = true;
  try {
    const languages = await fetchLanguages();
    languageOptions.value = languages as unknown as LanguageOption[];
    log('UserForm', 'loadLanguages', 'Languages loaded', languages);
  } catch (error: unknown) {
    log('UserForm', 'loadLanguages', 'Error loading languages', error);
    const message = error instanceof Error ? error.message : String(error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar idiomas') + ': ' + message);
  } finally {
    loadingLanguages.value = false;
  }
};

const loadGroups = async (): Promise<void> => {
  loadingGroups.value = true;
  try {
    const groups = await fetchUserGroups();
    availableGroups.value = groups as string[];
    log('UserForm', 'loadGroups', 'Groups loaded', groups);
  } catch (error: unknown) {
    log('UserForm', 'loadGroups', 'Error loading groups', error);
    const message = error instanceof Error ? error.message : String(error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar grupos') + ': ' + message);
  } finally {
    loadingGroups.value = false;
  }
};

const loadUserPermissions = async (): Promise<void> => {
  try {
    // Handle both direct user object and nested Usuario object from API response
    const userData = (props.user.Usuario || props.user) as User;
    const userSub = userData.sub;

    if (!userSub) {
      log('UserForm', 'loadUserPermissions', 'No user sub found', props.user);
      return;
    }

    const permissions = await fetchUserPermissions(userSub) as UserPermissions;

    // Handle Groups - now returned directly from pegaPermissoesUsuario
    userGroups.value = permissions.Groups || permissions.groups || [];

    // Handle Franquias (it's an object keyed by franchise ID, so extract the keys)
    const franquiasObj = permissions.Franquias || permissions.franquias || {};
    userFranchises.value = Object.keys(franquiasObj);

    // Initialize previous values to prevent initial API calls
    previousGroups = [...userGroups.value];
    previousFranchises = [...userFranchises.value];

    log('UserForm', 'loadUserPermissions', 'Permissions loaded', permissions);
    log('UserForm', 'loadUserPermissions', 'User groups:', JSON.stringify(userGroups.value));
    log('UserForm', 'loadUserPermissions', 'User franchises:', JSON.stringify(userFranchises.value));
  } catch (error: unknown) {
    log('UserForm', 'loadUserPermissions', 'Error loading permissions', error);
    const message = error instanceof Error ? error.message : String(error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar permissões') + ': ' + message);
  }
};

const loadFranchises = async (): Promise<void> => {
  loadingFranchises.value = true;
  try {
    const franchises = await permissionsService.getFranquias() as Record<string, FranchiseEntry>;

    // franchises is an object like { "1": { Dados: { Nome: "..." } }, "2": { Dados: { Nome: "..." } } }
    // Convert to array format with id and nome properties
    availableFranchises.value = Object.keys(franchises || {}).map(id => ({
      id: id,
      nome: franchises[id]?.Dados?.Nome || franchises[id]?.Nome || 'Sem nome'
    }));

    log('UserForm', 'loadFranchises', 'Franchises loaded', availableFranchises.value);
  } catch (error: unknown) {
    log('UserForm', 'loadFranchises', 'Error loading franchises', error);
    const message = error instanceof Error ? error.message : String(error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar franquias') + ': ' + message);
  } finally {
    loadingFranchises.value = false;
  }
};

const handleToggleGroup = async (group: string): Promise<void> => {
  log('UserForm', 'handleToggleGroup', 'Toggling group', group);
  togglingGroups.value[group] = true;

  try {
    // Handle both direct user object and nested Usuario object from API response
    const userData = (props.user.Usuario || props.user) as User;
    await toggleUserGroup(userData.username as string, group);
    eventBus.emit('globalMessage.success', t('Grupo atualizado com sucesso'));
    log('UserForm', 'handleToggleGroup', 'Group toggled successfully');
  } catch (error: unknown) {
    log('UserForm', 'handleToggleGroup', 'Error toggling group', error);
    const message = error instanceof Error ? error.message : String(error);
    eventBus.emit('globalMessage.error', t('Erro ao atualizar grupo') + ': ' + message);

    // Revert the change on error
    const index = userGroups.value.indexOf(group);
    const revertido = index > -1
      ? userGroups.value.filter(g => g !== group) // it was added but API failed
      : [...userGroups.value, group];             // it was removed but API failed
    // previousGroups acompanha o revert: sem isso o watcher leria a volta como uma mudança nova
    // e chamaria o toggle outra vez, desfazendo o que acabou de falhar.
    previousGroups = [...revertido];
    userGroups.value = revertido;
  } finally {
    togglingGroups.value[group] = false;
  }
};

const handleToggleFranchise = async (franquiaID: string): Promise<void> => {
  log('UserForm', 'handleToggleFranchise', 'Toggling franchise', franquiaID);
  togglingFranchises.value[franquiaID] = true;

  try {
    // Handle both direct user object and nested Usuario object from API response
    const userData = (props.user.Usuario || props.user) as User;
    await toggleUserFranquia(userData.sub as string, Number(franquiaID));
    eventBus.emit('globalMessage.success', t('Franquia atualizada com sucesso'));
    log('UserForm', 'handleToggleFranchise', 'Franchise toggled successfully');
  } catch (error: unknown) {
    log('UserForm', 'handleToggleFranchise', 'Error toggling franchise', error);
    const message = error instanceof Error ? error.message : String(error);
    eventBus.emit('globalMessage.error', t('Erro ao atualizar franquia') + ': ' + message);

    // Revert the change on error
    const index = userFranchises.value.indexOf(franquiaID);
    if (index > -1) {
      // Remove it (it was added but API failed)
      userFranchises.value = userFranchises.value.filter(f => f !== franquiaID);
    } else {
      // Add it back (it was removed but API failed)
      userFranchises.value = [...userFranchises.value, franquiaID];
    }
  } finally {
    togglingFranchises.value[franquiaID] = false;
  }
};

const handleSubmit = async (): Promise<void> => {
  if (!isFormValid.value) {
    eventBus.emit('globalMessage.error', t('Por favor, preencha todos os campos obrigatórios.'));
    return;
  }

  // Build the phone number using usePhoneFormat composable
  const phoneNumber = phoneToString(phoneModel.value);

  const userData = {
    ...formData.value,
    phone_number: phoneNumber,
  };

  log('UserForm', 'handleSubmit', 'Submitting info', userData);

  isSubmitting.value = true;

  try {
    await updateUser(userData);
    eventBus.emit('globalMessage.success', t('Usuário atualizado com sucesso'));
    log('UserForm', 'handleSubmit', 'User updated successfully');
    emit('save-success');
  } catch (error: unknown) {
    log('UserForm', 'handleSubmit', 'Error updating user', error);
    const message = error instanceof Error ? error.message : String(error);
    eventBus.emit('globalMessage.error', t('Erro ao atualizar usuário') + ': ' + message);
  } finally {
    isSubmitting.value = false;
  }
};

const loadUserData = async (): Promise<void> => {
  if (!props.user) {
    log('UserForm', 'loadUserData', 'No user provided');
    return;
  }

  log('UserForm', 'loadUserData', 'Loading user data', props.user);

  let userData: User;

  // Check if we only have a sub ID (need to fetch full data)
  if (props.user.sub && !props.user.username) {
    log('UserForm', 'loadUserData', 'Only sub provided, fetching full user data');
    try {
      userData = await fetchUserBySub(props.user.sub) as User;
      log('UserForm', 'loadUserData', 'Full user data fetched', userData);
    } catch (error: unknown) {
      log('UserForm', 'loadUserData', 'Error fetching user data', error);
      const message = error instanceof Error ? error.message : String(error);
      eventBus.emit('globalMessage.error', t('Erro ao carregar dados do usuário') + ': ' + message);
      return;
    }
  } else {
    // Handle both direct user object and nested Usuario object from API response
    userData = (props.user.Usuario || props.user) as User;
    log('UserForm', 'loadUserData', 'User data extracted from prop', userData);
  }

  // Update form data
  formData.value.username = userData.username || '';
  formData.value.name = userData.name || '';
  formData.value.documento = userData.documento || '';
  formData.value.email = userData.email || '';
  formData.value.phone_number = userData.phone_number || '';
  formData.value.language = userData.language || 'pt-BR';
  formData.value.bgcolor = userData.bgcolor || '#3c8dbc';
  formData.value.versaoSistema = String(userData.versaoSistema ?? '0');

  log('UserForm', 'loadUserData', 'Form data updated', JSON.parse(JSON.stringify(formData.value)));

  // Emit user-loaded event with full user data
  emit('user-loaded', userData);

  // Parse phone number using usePhoneFormat composable
  if (userData.phone_number) {
    log('UserForm', 'loadUserData', 'Parsing phone number', userData.phone_number);
    phoneModel.value = phoneFromString(userData.phone_number);
    log('UserForm', 'loadUserData', 'Phone model updated', JSON.parse(JSON.stringify(phoneModel.value)));
  }
};

const initializeForm = async (): Promise<void> => {
  log('UserForm', 'initializeForm', 'Initializing form');
  await loadUserData();
  loadLanguages();
  loadGroups();
  loadFranchises();
  loadUserPermissions();
};

// Expose public methods for parent components
defineExpose({
  canSave,
  handleSubmit,
  initializeForm,
  isSubmitting,
  activeTab
});
</script>

<style scoped>

.form-group {
  margin-bottom: 15px;
}

.text-danger {
  color: #dd4b39;
}

.text-center {
  text-align: center;
}

.form-control-static {
  padding-top: 7px;
  padding-bottom: 7px;
  margin-bottom: 0;
  min-height: 34px;
}

.input-group {
  display: flex;
  align-items: center;
}

.input-group-addon {
  background-color: #f4f4f4;
  border: 1px solid #d2d6de;
  padding: 6px 12px;
  font-size: 14px;
  font-weight: 400;
  color: #555;
  white-space: nowrap;
  border-radius: 4px 0 0 4px;
}

.info-message {
  padding: 15px;
  margin: 15px;
  border: 1px solid #bce8f1;
  border-radius: 4px;
  color: #31708f;
  background-color: #d9edf7;
  text-align: center;
}

/* PrimeVue Tabs styling */
:deep(.p-tablist) {
  margin-bottom: 1rem;
}

:deep(.p-tabpanels) {
  min-height: 300px;
  padding: 15px;
}
</style>
